import { getLogger } from "@marble/logger";
import { Prisma } from "@prisma/client";
import {
  DD_RESOURCE_NAME_TAG,
  DEFAULT_DURATION_MS_THRESHOLD,
  DEFAULT_HYDRATED_NODE_THRESHOLD,
  DEFAULT_RELATION_WALK_ROW_TRIGGER,
  DEFAULT_ROW_COUNT_THRESHOLD,
  DEFAULT_WALKS_PER_SECOND,
  DURATION_MS_THRESHOLD_ENV_VAR,
  HYDRATED_NODE_THRESHOLD_ENV_VAR,
  HYDRATION_WALK_DISABLED_ENV_VAR,
  RELATION_WALK_ROW_TRIGGER_ENV_VAR,
  ROW_COUNT_THRESHOLD_ENV_VAR,
  SLOW_QUERY_LOG_MESSAGE,
  WALKS_PER_SECOND_ENV_VAR,
} from "./constants";
import { measureHydratedNodes } from "@marble/db/query-instrumentation/hydration-walk";
import { describeIncludeShape } from "@marble/db/query-instrumentation/include-shape";
import {
  createWalkBudget,
  SKIP_REPORT_FLUSH_THRESHOLD,
} from "@marble/db/query-instrumentation/walk-budget";
import type {
  WalkBudget,
  WalkBudgetDecision,
  WalkCategory,
} from "@marble/db/query-instrumentation/walk-budget";
import type {
  QueryInstrumentationDatasource,
  QueryInstrumentationOptions,
} from "./types";

const logger = getLogger("db:query-instrumentation");

/**
 * The `$allOperations` handler that does the measuring, split out from the
 * extension itself so it can be unit-tested without standing up a client.
 *
 * Production OOMs in this codebase are driven by request-scoped bulk row
 * hydration — millions of small plain objects — so hydrated object count is
 * the instrument that matters. Unlike APM this is unsampled, it sees the
 * *arguments* rather than a normalized route, and it sees non-HTTP callers
 * (crons, event consumers, webhook fan-out).
 *
 * There are two tiers, because top-level `rowCount` is blind to `include`
 * fan-out — a query returning 50 rows with a deep include can hydrate
 * hundreds of thousands of objects and report 50:
 *
 * - **Tier 1, every query, allocation-free.** Elapsed time and, for array
 *   results, length. Two integers, one `Array.isArray`, three comparisons. No
 *   object construction, no string building, no `process.env`, no
 *   `memoryUsage()`, no tracer lookup, no look at the result's contents.
 * - **Tier 2, only once tier 1 has already fired.** Derive the `include`
 *   fan-out shape from the arguments (O(args)), and — when the *row count*
 *   trigger fired and the per-second walk budget allows — walk the result
 *   graph under hard node and depth caps to count what was actually hydrated
 *   and name the relations responsible.
 *
 * Tier 2's expensive half is gated on row count and a rate limit rather than
 * on duration, so the instrument's blocking CPU is a constant. Duration is a
 * property of the pod's health, not of the query: under GC pressure even a
 * `take: 10` query has been seen taking seconds in production, so a duration
 * trigger would schedule the most work at the moment the pod could least
 * afford it.
 *
 * Thresholds and triggers are resolved once, here, so the per-query path never
 * touches `process.env`.
 */
export const createQueryInstrumentationHandler = ({
  datasource = "primary",
}: QueryInstrumentationOptions = {}) => {
  const rowCountThreshold = _readThreshold(
    ROW_COUNT_THRESHOLD_ENV_VAR,
    DEFAULT_ROW_COUNT_THRESHOLD,
  );
  const durationMsThreshold = _readThreshold(
    DURATION_MS_THRESHOLD_ENV_VAR,
    DEFAULT_DURATION_MS_THRESHOLD,
  );
  const hydratedNodeThreshold = _readThreshold(
    HYDRATED_NODE_THRESHOLD_ENV_VAR,
    DEFAULT_HYDRATED_NODE_THRESHOLD,
  );
  const relationWalkRowTrigger = _readThreshold(
    RELATION_WALK_ROW_TRIGGER_ENV_VAR,
    DEFAULT_RELATION_WALK_ROW_TRIGGER,
  );
  const hydrationWalkEnabled =
    process.env[HYDRATION_WALK_DISABLED_ENV_VAR] !== "true";
  const walkBudget = createWalkBudget(
    _readThreshold(WALKS_PER_SECOND_ENV_VAR, DEFAULT_WALKS_PER_SECOND),
  );

  return async <TArgs, TResult>({
    model,
    operation,
    args,
    query,
  }: {
    model?: string;
    operation: string;
    args: TArgs;
    query: (args: TArgs) => Promise<TResult>;
  }): Promise<TResult> => {
    const startedAt = Date.now();
    const result = await query(args);
    const durationMs = Date.now() - startedAt;
    const rowCount = Array.isArray(result) ? result.length : undefined;

    const exceededThreshold =
      (rowCount !== undefined && rowCount > rowCountThreshold) ||
      durationMs > durationMsThreshold;

    // Everything above this line runs on every query in every runtime and
    // allocates nothing. Everything below is gated on a cheap trigger having
    // already fired.
    const rowCountTriggered =
      rowCount !== undefined && rowCount >= relationWalkRowTrigger;
    // A `findUnique`/`findFirst` has no row count to gate on, so it is
    // sampled instead — but only when it actually projects relations, which
    // is two property reads and no traversal. ~249 of the app's ~1,208
    // single-result call sites carry an `include`, several of them shared
    // constants on high-traffic pages, so "one row, arbitrarily large graph"
    // is not a shape we can afford to be structurally blind to.
    const singleResultCandidate =
      rowCount === undefined && _hasProjection(args);

    if (exceededThreshold || rowCountTriggered || singleResultCandidate) {
      _measureAndMaybeLog({
        model,
        operation,
        args,
        result,
        rowCount,
        durationMs,
        datasource,
        exceededThreshold,
        rowCountTriggered,
        singleResultCandidate,
        hydratedNodeThreshold,
        hydrationWalkEnabled,
        walkBudget,
      });
    }

    return result;
  };
};

/**
 * Prisma client extension that logs every model operation returning an
 * unexpectedly large row set or object graph, or taking an unexpectedly long
 * time.
 */
export const createQueryInstrumentationExtension = (
  options: QueryInstrumentationOptions = {},
) =>
  Prisma.defineExtension({
    name: "marble-query-instrumentation",
    query: {
      $allModels: {
        $allOperations: createQueryInstrumentationHandler(options),
      },
    },
  });

const _readThreshold = (envVar: string, fallback: number) => {
  const raw = process.env[envVar];
  if (raw === undefined || raw === "") {
    return fallback;
  }

  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return fallback;
  }

  return parsed;
};

/**
 * Best-effort route attribution from the active dd-trace span.
 *
 * `@marble/db` is imported by Lambdas, the standalone services and one-off
 * scripts as well as the Next.js app, so dd-trace may be absent or
 * uninitialized. We therefore read the tracer off `global._ddtrace` — which
 * dd-trace only defines once it has been required — instead of importing it,
 * so this package neither gains a dependency on dd-trace nor initializes it as
 * a side effect. Every access is defensive: this returns `undefined` rather
 * than throwing.
 */
const _getActiveSpanResourceName = (): string | undefined => {
  try {
    const tracer = (globalThis as { _ddtrace?: any })._ddtrace;
    if (!tracer || typeof tracer.scope !== "function") {
      return undefined;
    }

    const span = tracer.scope().active();
    if (!span || typeof span.context !== "function") {
      return undefined;
    }

    const spanContext = span.context();
    // The active span inside a query is usually the DB span; the route/action
    // lives on the local root span, which dd-trace keeps first in the trace's
    // started list.
    const localRoot = spanContext?._trace?.started?.[0];
    const localRootContext =
      typeof localRoot?.context === "function"
        ? localRoot.context()
        : undefined;

    return (
      localRootContext?._tags?.[DD_RESOURCE_NAME_TAG] ??
      spanContext?._tags?.[DD_RESOURCE_NAME_TAG] ??
      localRootContext?._name ??
      spanContext?._name ??
      undefined
    );
  } catch {
    return undefined;
  }
};

/**
 * Process heap at the moment the query returned.
 *
 * Deliberately not a before/after delta: taking the "before" reading would put
 * a `memoryUsage()` call on every query, which is exactly the hot-path cost
 * this design refuses to pay. And it is **indicative, not attributable** —
 * concurrent requests share this heap, so a large value means "this pod was
 * under memory pressure when this query landed", never "this query allocated
 * that much". `hydratedNodeCount` is the attributable number.
 */
const _readHeapUsedMb = (): number | undefined => {
  try {
    return Math.round(process.memoryUsage().heapUsed / 1024 / 1024);
  } catch {
    return undefined;
  }
};

/**
 * The tier-2 path: everything expensive, everything optional, nothing allowed
 * to throw into a caller. Any failure here degrades to the tier-1 behaviour
 * that shipped in #7173 — or, if the logger itself is the thing failing, to
 * silence.
 */
const _measureAndMaybeLog = ({
  model,
  operation,
  args,
  result,
  rowCount,
  durationMs,
  datasource,
  exceededThreshold,
  rowCountTriggered,
  singleResultCandidate,
  hydratedNodeThreshold,
  hydrationWalkEnabled,
  walkBudget,
}: {
  model?: string;
  operation: string;
  args: unknown;
  result: unknown;
  rowCount: number | undefined;
  durationMs: number;
  datasource: QueryInstrumentationDatasource;
  exceededThreshold: boolean;
  rowCountTriggered: boolean;
  singleResultCandidate: boolean;
  hydratedNodeThreshold: number;
  hydrationWalkEnabled: boolean;
  walkBudget: WalkBudget;
}) => {
  try {
    // Nothing here classifies relations from the arguments. An earlier
    // revision gated on `includeShape !== undefined` and Bugbot caught the
    // hole: inside a `select`, `Relation: true` is indistinguishable from a
    // scalar column without the schema, so `select: { Relation: true }` — a
    // common form — produced no shape and therefore never walked, which is
    // the exact blind spot this change exists to close. The only argument
    // check that survives is `_hasProjection`, which asks whether a
    // projection exists at all rather than what is in it, so it cannot make
    // that mistake.
    //
    // What does gate the walk is the category trigger (row count, never
    // duration — see RELATION_WALK_ROW_TRIGGER) and the budget, which pins
    // worst-case blocking CPU to a constant instead of something that grows
    // as the pod gets sicker.
    const walkCategory: WalkCategory =
      rowCount === undefined ? "single-result" : "row-set";
    const eligible =
      hydrationWalkEnabled && (rowCountTriggered || singleResultCandidate);
    const decision: WalkBudgetDecision | "not-eligible" = eligible
      ? walkBudget.tryConsume(walkCategory, model, operation)
      : "not-eligible";

    // Rate limiting must not be able to silence the very query it matters
    // most to see. A 100-4999 row `include` query crosses no threshold by
    // itself, so a denied walk would otherwise produce nothing at all and
    // leave its skip count waiting on an unrelated query to carry it out —
    // an unbounded wait under sustained load. Once skips accumulate, the
    // report forces a line of its own.
    const mustFlushSkipReport =
      walkBudget.pendingSkippedCount() >= SKIP_REPORT_FLUSH_THRESHOLD;

    // Nothing left to contribute: no walk, no threshold crossed that would
    // justify a line, and nothing owed. Bail before the argument-shape work.
    if (decision !== "granted" && !exceededThreshold && !mustFlushSkipReport) {
      return;
    }

    const includeShape = _tryDescribeIncludeShape(args);
    const hydration =
      decision === "granted" ? _tryMeasureHydratedNodes(result) : undefined;

    const exceededHydrationThreshold =
      hydration !== undefined && hydration.nodeCount > hydratedNodeThreshold;

    if (
      !exceededThreshold &&
      !exceededHydrationThreshold &&
      !mustFlushSkipReport
    ) {
      return;
    }

    // Taken after the walk, so it includes the walk's own bounded stack — a
    // few MB against a heap measured in hundreds. One more reason the field
    // is named "indicative".
    const heapUsedMb = _readHeapUsedMb();
    // Drained only now, when a line is definitely being emitted, so a
    // rate-limited measurement surfaces as a number rather than vanishing.
    const skipReport = walkBudget.drainSkipped();

    let take: unknown = null;
    if (typeof args === "object" && args !== null && "take" in args) {
      take = (args as { take?: unknown }).take ?? null;
    }

    logger.warn(
      {
        model,
        operation,
        rowCount,
        durationMs,
        take,
        datasource,
        resourceName: _getActiveSpanResourceName(),
        hydratedNodeCount: hydration?.nodeCount,
        hydrationWalkSkippedReason: _walkSkippedReason({
          decision,
          hydrationWalkEnabled,
          walkCategory,
        }),
        hydrationWalksSkipped: skipReport.count,
        hydrationWalksSkippedFor:
          skipReport.queries.length > 0 ? skipReport.queries : undefined,
        hydrationTruncated: hydration?.truncated,
        hydrationDepthCapped: hydration?.depthCapped,
        hydrationMaxDepth: hydration?.maxDepth,
        topHydratedRelations: hydration?.topRelations.map(
          ({ relation, nodeCount }) => ({
            relation: model === undefined ? relation : `${model}.${relation}`,
            nodeCount,
          }),
        ),
        includeDepth: includeShape?.depth,
        includeRelationCount: includeShape?.relationCount,
        includeRelations: includeShape?.relations,
        includeShapeTruncated: includeShape?.truncated,
        heapUsedMbIndicative: heapUsedMb,
      },
      SLOW_QUERY_LOG_MESSAGE,
    );
  } catch {
    // Instrumentation must never break a caller's query.
  }
};

/**
 * Why this line carries no `hydratedNodeCount`, so a gap in the data is never
 * ambiguous. `undefined` means the walk ran.
 */
const _walkSkippedReason = ({
  decision,
  hydrationWalkEnabled,
  walkCategory,
}: {
  decision: WalkBudgetDecision | "not-eligible";
  hydrationWalkEnabled: boolean;
  walkCategory: WalkCategory;
}) => {
  if (decision === "granted") {
    return undefined;
  }
  if (!hydrationWalkEnabled) {
    return "kill-switch";
  }
  if (decision === "not-eligible") {
    return walkCategory === "single-result"
      ? "no-projection"
      : "below-row-trigger";
  }

  return decision;
};

/**
 * Whether the query projects anything at all — not *what* it projects. Two
 * property reads, no traversal, no values read. Used only to decide whether a
 * single result is worth sampling; a bare `findUnique({ where })` cannot
 * hydrate a graph, so there is nothing to measure.
 */
const _hasProjection = (args: unknown) => {
  if (typeof args !== "object" || args === null) {
    return false;
  }

  const { include, select } = args as {
    include?: unknown;
    select?: unknown;
  };

  return (
    (typeof include === "object" && include !== null) ||
    (typeof select === "object" && select !== null)
  );
};

const _tryDescribeIncludeShape = (args: unknown) => {
  try {
    return describeIncludeShape(args);
  } catch {
    return undefined;
  }
};

const _tryMeasureHydratedNodes = (result: unknown) => {
  try {
    return measureHydratedNodes(result);
  } catch {
    return undefined;
  }
};
